// ============================================================
// creacionesPage.js — Página "Mis Creaciones"
// ------------------------------------------------------------
// Carga las creaciones del usuario desde la API y las muestra
// en la cuadrícula de la galería.
// ============================================================
import { qs, qsa, on } from './utils.js';
import { listHistory } from './api.js';
import { toast } from './toast.js';

export async function initCreacionesPage() {
  if (document.body.dataset.page !== 'creaciones') return;

  const grid = qs('.gallery-grid');
  if (!grid) return;

  // Limpiar placeholders estáticos
  grid.innerHTML = `
    <div class="text-center py-5 w-100" id="creationsLoading">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-2 text-muted">Cargando tus creaciones...</p>
    </div>
  `;

  try {
    const items = await listHistory();
    const completedItems = items.filter(item => item.status === 'completed');

    grid.innerHTML = ''; // Limpiar spinner

    if (completedItems.length === 0) {
      grid.innerHTML = `
        <div class="text-center py-5 w-100">
          <i class="bi bi-folder-x" style="font-size: 3rem; color: var(--text-muted);"></i>
          <h3 class="mt-3">No tienes creaciones</h3>
          <p class="text-muted">Las imágenes, vídeos o avatares que generes aparecerán aquí.</p>
        </div>
      `;
      return;
    }

    // Elementos del modal de vista previa
    const modal = qs('#previewModal');
    const mediaContainer = qs('#previewMediaContainer');
    const promptEl = qs('#previewPrompt');
    const closeBtn = qs('#closePreviewBtn');

    function openPreviewModal(item) {
      if (!modal || !mediaContainer || !promptEl) return;
      
      mediaContainer.innerHTML = '';
      
      const isVideo = item.type === 'video' || item.type === 'avatar' || item.type === 'template';
      if (isVideo) {
        const video = document.createElement('video');
        video.src = item.url;
        video.controls = true;
        video.autoplay = true;
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'contain';
        mediaContainer.appendChild(video);
      } else {
        const img = document.createElement('img');
        img.src = item.url;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        mediaContainer.appendChild(img);
      }
      
      // Mapear item.prompt a item.title ya que en listHistory() se mapea g.prompt a title
      promptEl.textContent = item.title || item.prompt || '';
      
      modal.style.display = 'flex';
      setTimeout(() => {
        modal.style.opacity = '1';
        modal.firstElementChild.style.transform = 'scale(1)';
      }, 10);
    }

    function closePreviewModal() {
      if (!modal) return;
      modal.style.opacity = '0';
      modal.firstElementChild.style.transform = 'scale(0.9)';
      
      const video = mediaContainer?.querySelector('video');
      if (video) {
        video.pause();
      }
      
      setTimeout(() => {
        modal.style.display = 'none';
        if (mediaContainer) mediaContainer.innerHTML = '';
      }, 300);
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closePreviewModal);
    }
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closePreviewModal();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.style.display === 'flex') {
        closePreviewModal();
      }
    });

    completedItems.forEach(item => {
      const card = document.createElement('div');
      card.className = 'gen-card has-result';
      card.style.cursor = 'pointer';
      
      const isVideo = item.type === 'video' || item.type === 'avatar' || item.type === 'template';
      const isImageUrl = item.url && /\.(png|jpe?g|gif|webp|avif)$/i.test(item.url);
      
      let mediaHtml = '';
      if (isVideo && !isImageUrl) {
        mediaHtml = `
          <video src="${item.url}" poster="${item.thumb || ''}" muted loop playsinline style="width:100%; height:100%; object-fit:cover; border-radius:12px;"></video>
          <span class="history-thumb-play" style="position:absolute; top:50%; left:50%; transform:translate(-50%, -50%); background:rgba(0,0,0,0.6); color:white; border-radius:50%; width:40px; height:40px; display:flex; align-items:center; justify-content:center; pointer-events:none;">
            <i class="bi bi-play-fill" style="font-size: 1.5rem; margin-left: 2px;"></i>
          </span>
        `;
      } else {
        mediaHtml = `<img src="${item.url}" alt="${item.title}" style="width:100%; height:100%; object-fit:cover; border-radius:12px;" />`;
      }

      card.innerHTML = `
        ${mediaHtml}
        <div class="gen-card-actions">
          <button class="gen-card-action" type="button" aria-label="Descargar" data-download-url="${item.url}">
            <i class="bi bi-download"></i>
          </button>
        </div>
      `;

      // Evento de clic para vista previa en modal con audio
      card.addEventListener('click', (e) => {
        if (e.target.closest('.gen-card-action')) return;
        openPreviewModal(item);
      });

      // Autoplay al pasar el ratón si es vídeo real (silenciado en grid)
      if (isVideo && !isImageUrl) {
        const video = card.querySelector('video');
        if (video) {
          card.addEventListener('mouseenter', () => video.play().catch(() => {}));
          card.addEventListener('mouseleave', () => {
            video.pause();
            video.currentTime = 0;
          });
        }
      }

      grid.appendChild(card);
    });

  } catch (err) {
    console.error('[Creaciones] Error al cargar creaciones:', err);
    grid.innerHTML = `
      <div class="alert alert-danger w-100" role="alert">
        Error al cargar tus creaciones. Asegúrate de haber iniciado sesión.
      </div>
    `;
  }
}
